/* Activity timeline for the CRM contact and client drawers.

   Variant A of mockups/crm-activity-timeline.html: one column, inside the
   drawer, above Notes, with filter chips. Read only, always. Nothing here
   writes to Firestore, and an unreadable collection renders one muted line
   rather than throwing. */
import { useMemo, useState } from "react";
import { C, lbl, chip } from "../../lib/styles.js";
import { fm, fmt } from "../../lib/utils.js";
import {
  ACTIVITY_CHIPS,
  bodyFooter,
  bodyOmittedNotice,
  detailLine,
  filterRecords,
  fmtDate,
  gmailUrl,
  isOpenItem,
  kindLabel,
  senderLabel,
} from "./activityModel.js";

/* The mockup's --dim, one step lighter than C.muted. styles.js has no token
   for it and is shared with every other app, so it stays local. */
const DIM = "#9A9691";

const DOT_COLORS = {
  message: C.blue,
  quote: C.amber,
  agreement: C.purple,
  preference: C.teal,
  open_item: C.danger,
  session: C.capture,
  note: C.muted,
};

const S = {
  head: { display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 },
  hint: { fontSize: 11, color: DIM, marginLeft: "auto", fontFamily: fm },
  chips: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 },
  row: {
    display: "grid", gridTemplateColumns: "62px 16px minmax(0,1fr)", gap: "0 10px",
    padding: "9px 0",
  },
  when: {
    fontFamily: fm, fontSize: 10.5, color: DIM, paddingTop: 3,
    whiteSpace: "pre-line", lineHeight: 1.35,
  },
  line1: { display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap", fontSize: 12.5 },
  kind: {
    fontFamily: fm, fontSize: 9.5, letterSpacing: "0.1em", textTransform: "uppercase", color: DIM,
  },
  who: { fontWeight: 500 },
  line2: { fontSize: 11.5, color: C.muted, marginTop: 2 },
  meta: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 7 },
  amt: { fontFamily: fm, fontSize: 12, color: C.text },
  handle: { fontFamily: fm, fontSize: 10.5, color: C.muted },
  src: {
    fontFamily: fm, fontSize: 9.5, color: DIM, border: `1px solid ${C.border}`,
    borderRadius: 3, padding: "0 4px", lineHeight: "15px", display: "inline-block",
  },
  gmail: { fontSize: 10.5, color: C.blue, textDecoration: "none", whiteSpace: "nowrap" },
  box: {
    width: 12, height: 12, border: `1px solid ${C.border}`, borderRadius: 3,
    display: "inline-block", verticalAlign: -2, marginRight: 6, background: C.surface,
    flexShrink: 0,
  },
  boxOn: {
    background: C.green, borderColor: C.green, color: "#FFF", fontSize: 9,
    lineHeight: "11px", textAlign: "center",
  },
  toggle: {
    marginTop: 6, fontFamily: fm, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
    color: DIM, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4,
    padding: "2px 7px", cursor: "pointer",
  },
  fulltext: {
    marginTop: 8, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6,
    padding: "11px 13px", fontSize: 12.5, color: C.text, whiteSpace: "pre-wrap", lineHeight: 1.6,
  },
  atts: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 },
  att: {
    display: "inline-flex", alignItems: "center", gap: 5, fontFamily: fm, fontSize: 10.5,
    color: C.muted, background: C.surface, border: `1px solid ${C.border}`,
    borderRadius: 5, padding: "3px 8px",
  },
  stripnote: {
    fontFamily: fm, fontSize: 10, color: DIM, marginTop: 8, paddingTop: 7,
    borderTop: `1px solid ${C.border}`,
  },
  empty: { fontSize: 12, color: C.muted },
};

function Rail({ color, isLast }) {
  return (
    <div style={{ position: "relative" }}>
      <span style={{
        position: "absolute", left: "50%", top: 0, bottom: isLast ? "50%" : -18,
        width: 1, background: C.border, transform: "translateX(-0.5px)",
      }} />
      <span style={{
        position: "absolute", left: "50%", top: 6, width: 7, height: 7, borderRadius: 2,
        transform: "translateX(-50%)", background: color,
      }} />
    </div>
  );
}

function Row({ record: r, isLast, isFirst, expanded, onToggle, now }) {
  const done = r.type === "open_item" && (r.status || "open") === "done";
  const dot = done ? C.green : (DOT_COLORS[r.type] || C.muted);
  const kind = kindLabel(r);
  const line2 = detailLine(r);
  const link = r.type === "message" ? gmailUrl(r.gmail?.threadId) : null;
  const body = typeof r.body === "string" && r.body.trim() ? r.body : "";
  const omitted = bodyOmittedNotice(r);
  const atts = Array.isArray(r.attachments) ? r.attachments.filter(Boolean) : [];
  const handle = r.type === "quote" ? r.quoteId : r.type === "session" ? r.jobId : null;
  const amount = r.type === "quote" && Number.isFinite(Number(r.amount)) ? Number(r.amount) : null;
  const who = r.type === "message" ? senderLabel(r) : "";

  return (
    <div style={{ ...S.row, borderTop: isFirst ? "none" : `1px solid ${C.border}22` }}>
      <div style={S.when}>{fmtDate(r.date || r.at, now)}</div>
      <Rail color={dot} isLast={isLast} />
      <div>
        <div style={S.line1}>
          {r.type === "message" && r.direction && (
            <span style={{
              fontFamily: fm, fontSize: 11,
              color: r.direction === "in" ? C.blue : C.stone,
            }}>
              {r.direction === "in" ? "↓" : "↑"}
            </span>
          )}
          {who && <span style={S.who}>{who}</span>}
          {kind && <span style={S.kind}>{kind}</span>}
          {r.type === "quote" && handle && <span style={S.handle}>{handle}</span>}
          {r.type === "open_item" && (
            <span style={done ? { ...S.box, ...S.boxOn } : S.box}>{done ? "✓" : ""}</span>
          )}
          <span style={done
            ? { color: C.muted, textDecoration: "line-through", textDecorationColor: DIM }
            : { color: C.text }}>
            {r.summary || ""}
          </span>
          <span style={S.meta}>
            {amount !== null && <span style={S.amt}>{fmt(amount)}</span>}
            {r.type === "session" && handle && <span style={S.handle}>{handle}</span>}
            {link && (
              <a style={S.gmail} href={link} target="_blank" rel="noopener noreferrer">
                Gmail {"↗"}
              </a>
            )}
            {r.source && <span style={S.src}>{r.source}</span>}
          </span>
        </div>

        {line2 && <div style={S.line2}>{line2}</div>}

        {r.type === "message" && omitted && (
          <div style={S.line2}>{omitted}</div>
        )}

        {r.type === "message" && !omitted && body && (
          <div>
            <button type="button" style={S.toggle} onClick={onToggle}>
              Full message {expanded ? "↑" : "↓"}
            </button>
            {expanded && (
              <div>
                <div style={S.fulltext}>{body}</div>
                {atts.length > 0 && (
                  <div style={S.atts}>
                    {atts.map((a, i) => (
                      <span key={(a.filename || "att") + i} style={S.att}>
                        {"📎"} {a.filename || "Attachment"}
                        {a.size ? <span style={{ color: DIM }}>{a.size}</span> : null}
                      </span>
                    ))}
                  </div>
                )}
                {/* Every stored body is stripped by the writer, so this is
                    unconditional under an expanded message. */}
                <div style={S.stripnote}>{bodyFooter(r)}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ActivityTimeline -- read-only interactions timeline.
 *
 * @param records  interactions docs, already sorted newest first by useInteractions
 * @param loading  true while the first snapshot is outstanding
 * @param error    a Firestore error (permission-denied until the rule ships)
 * @param variant  "contact" or "client", changes the record count wording only
 */
export default function ActivityTimeline({ records, loading, error, variant = "contact" }) {
  const [activeChip, setActiveChip] = useState("all");
  const [expanded, setExpanded] = useState({});
  const all = useMemo(() => (records || []).filter(Boolean), [records]);
  const shown = useMemo(() => filterRecords(all, activeChip), [all, activeChip]);
  const now = useMemo(() => new Date(), []);

  const heading = <span style={lbl}>Activity</span>;

  if (error) {
    return (
      <div>
        <div style={S.head}>{heading}</div>
        <div style={S.empty}>Activity is not available for this account.</div>
      </div>
    );
  }

  if (loading && all.length === 0) {
    return (
      <div>
        <div style={S.head}>{heading}</div>
        <div style={S.empty}>Loading activity.</div>
      </div>
    );
  }

  const openN = all.filter(isOpenItem).length;
  const count = `${all.length} record${all.length === 1 ? "" : "s"}${variant === "client" ? ", all people" : ""}`;

  return (
    <div>
      <div style={S.head}>
        {heading}
        <span style={S.hint}>{count}</span>
      </div>

      {all.length > 0 && (
        <div style={S.chips}>
          {ACTIVITY_CHIPS.map(c => (
            <span
              key={c.key}
              role="button"
              tabIndex={0}
              style={chip(activeChip === c.key)}
              onClick={() => setActiveChip(c.key)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveChip(c.key); } }}
            >
              {c.key === "open" && openN > 0 ? `${c.label} (${openN})` : c.label}
            </span>
          ))}
        </div>
      )}

      {all.length === 0 && <div style={S.empty}>No activity recorded yet.</div>}

      {all.length > 0 && shown.length === 0 && (
        <div style={S.empty}>No records of this kind.</div>
      )}

      {shown.length > 0 && (
        <div style={{ position: "relative" }}>
          {shown.map((r, i) => (
            <Row
              key={r.id || i}
              record={r}
              isFirst={i === 0}
              isLast={i === shown.length - 1}
              now={now}
              expanded={Boolean(expanded[r.id])}
              onToggle={() => setExpanded(prev => ({ ...prev, [r.id]: !prev[r.id] }))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
